import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  countFamiliesNeedingRerun,
  moveBpaSyncFailureReport,
  pipelineMoveBpaService,
  type MoveBpaRequest,
  type MoveBpaResponse,
} from "../../../../api/pipelineMoveBpa.service.js";
import { preventUnload } from "../BatchUpdates/batchRunHelpers.js";
import { describeApiError } from "../BatchUpdates/describeApiError.js";
import type { MoveBpaCsvRow } from "./parseMoveBpaCsv.js";

export type MoveBpaRowStatus = "ok" | "partial" | "failed" | "unknown";

export type MoveBpaRowResult = {
  row: MoveBpaCsvRow;
  status: MoveBpaRowStatus;
  report: MoveBpaResponse | null;
  error: string | null;
};

export type MoveBpaProgress = { processedRows: number; totalRows: number };

export type MoveBpaTotals = {
  rows: number;
  ok: number;
  partial: number;
  failed: number;
  unknown: number;
  roots: number;
  children: number;
  alreadyInPlace: number;
  movedApprovalRequests: number;
  movedTestimonials: number;
  movedProcessingItems: number;
  approvalFailures: number;
  familiesNeedingRerun: number;
  warnings: number;
};

export type MoveBpaRunSummary = { finished: boolean; results: MoveBpaRowResult[] };

const PARTIAL_FALLBACK = "Approval sync failed for some families";

const UNKNOWN_SUFFIX = "outcome unknown, rerun to confirm";

export const EMPTY_PROGRESS: MoveBpaProgress = { processedRows: 0, totalRows: 0 };

export const EMPTY_TOTALS: MoveBpaTotals = {
  rows: 0,
  ok: 0,
  partial: 0,
  failed: 0,
  unknown: 0,
  roots: 0,
  children: 0,
  alreadyInPlace: 0,
  movedApprovalRequests: 0,
  movedTestimonials: 0,
  movedProcessingItems: 0,
  approvalFailures: 0,
  familiesNeedingRerun: 0,
  warnings: 0,
};

export function toRequest(row: MoveBpaCsvRow, dryRun: boolean): MoveBpaRequest {
  return {
    firmRef: row.firmRef,
    publicationId: row.publicationId,
    sourcePracticeAreaIds: row.sourcePracticeAreaIds,
    target: row.target,
    ...(row.expectedItems === null ? {} : { expectedItems: row.expectedItems }),
    dryRun,
  };
}

export function tally(results: MoveBpaRowResult[]): MoveBpaTotals {
  const totals = { ...EMPTY_TOTALS };
  for (const { status, report } of results) {
    totals.rows += 1;
    totals[status] += 1;
    if (!report) continue;
    totals.roots += report.matched.roots;
    totals.children += report.matched.children;
    totals.alreadyInPlace += report.matched.alreadyInPlace;
    totals.movedApprovalRequests += report.moved.approvalRequests;
    totals.movedTestimonials += report.moved.testimonials;
    totals.movedProcessingItems += report.moved.processingItems;
    totals.approvalFailures += report.approvalSync.failed.length;
    totals.familiesNeedingRerun += countFamiliesNeedingRerun(report);
    totals.warnings += report.warnings.length;
  }
  return totals;
}

function isRejected(err: unknown): boolean {
  const status = err instanceof Error ? (err as Error & { status?: unknown }).status : undefined;
  return typeof status === "number" && status >= 400 && status <= 499;
}

async function moveRow(row: MoveBpaCsvRow, dryRun: boolean): Promise<MoveBpaRowResult> {
  try {
    const report = await pipelineMoveBpaService.moveBpa(toRequest(row, dryRun));
    return { row, status: "ok", report, error: null };
  } catch (err) {
    const partial = moveBpaSyncFailureReport(err);
    if (partial) {
      const message = err instanceof Error && err.message ? err.message : PARTIAL_FALLBACK;
      return { row, status: "partial", report: partial, error: message };
    }
    const detail = describeApiError(err, "Move failed");
    if (dryRun || isRejected(err)) return { row, status: "failed", report: null, error: detail };
    return { row, status: "unknown", report: null, error: `${detail} — ${UNKNOWN_SUFFIX}` };
  }
}

export function useMoveBpaBatch() {
  const [running, setRunning] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [progress, setProgress] = useState<MoveBpaProgress>(EMPTY_PROGRESS);
  const [results, setResults] = useState<MoveBpaRowResult[]>([]);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!running) return;
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [running]);

  const stop = useCallback(() => {
    stopRef.current = true;
    setStopping(true);
  }, []);

  const reset = useCallback(() => {
    setStopped(false);
    setDryRun(true);
    setProgress(EMPTY_PROGRESS);
    setResults([]);
  }, []);

  const start = useCallback(
    async (rows: MoveBpaCsvRow[], isDryRun: boolean): Promise<MoveBpaRunSummary> => {
      const totalRows = rows.length;
      const collected: MoveBpaRowResult[] = [];

      stopRef.current = false;
      setRunning(true);
      setStopping(false);
      setStopped(false);
      setDryRun(isDryRun);
      setResults([]);
      setProgress({ processedRows: 0, totalRows });

      try {
        for (const row of rows) {
          if (stopRef.current) {
            setStopped(true);
            return { finished: false, results: collected };
          }
          collected.push(await moveRow(row, isDryRun));
          setResults([...collected]);
          setProgress({ processedRows: collected.length, totalRows });
        }
        return { finished: true, results: collected };
      } finally {
        setRunning(false);
        setStopping(false);
      }
    },
    [],
  );

  const totals = useMemo(() => tally(results), [results]);

  return useMemo(
    () => ({ running, stopping, stopped, dryRun, progress, results, totals, start, stop, reset }),
    [running, stopping, stopped, dryRun, progress, results, totals, start, stop, reset],
  );
}
